import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Database, Plus, Check, Edit, Trash2, FileText, AlertCircle,
  Copy, Download, Upload, Globe, Building2, Users
} from "lucide-react";

const STATUS_BADGE = {
  approved: "bg-[#E5F0F2] text-[#006080]",
  draft: "bg-slate-100 text-slate-600",
};

// ── Allocate Modal ─────────────────────────────────────────────────────────────

function AllocateModal({ schema, companies, onClose, onSave }) {
  const [selected, setSelected] = useState(new Set(schema.allocated_company_ids || []));
  const [saving, setSaving] = useState(false);

  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/schemas/${schema.id}/allocate`, { company_ids: [...selected] });
      onSave();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <Users size={14} className="text-[#006080]" />
          <div>
            <h3 className="font-bold text-slate-900 text-sm">Allocate "{schema.name}"</h3>
            <p className="text-xs text-slate-400">Select which companies can use this global schema</p>
          </div>
        </div>
        <div className="p-4 max-h-64 overflow-y-auto space-y-1">
          {companies.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No companies found</p>}
          {companies.map(co => (
            <label key={co.id} className="flex items-center gap-2.5 p-2 rounded-md hover:bg-slate-50 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.has(co.id)}
                onChange={() => toggle(co.id)}
                className="w-4 h-4 rounded border-slate-300 text-[#006080] focus:ring-[#006080]"
              />
              <span className="text-sm text-slate-700">{co.name}</span>
            </label>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex gap-2">
          <button onClick={onClose} className="flex-1 h-8 border border-slate-200 text-sm text-slate-600 rounded-md hover:bg-slate-50">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 h-8 bg-[#006080] text-white text-sm font-medium rounded-md disabled:opacity-50">
            {saving ? "Saving…" : "Save Allocation"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Import Modal ───────────────────────────────────────────────────────────────

function ImportModal({ onClose, onSave }) {
  const [json, setJson] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = ev => setJson(ev.target.result);
    reader.readAsText(f);
  };

  const handleImport = async () => {
    setError("");
    let parsed;
    try { parsed = JSON.parse(json); } catch { setError("Invalid JSON"); return; }
    setSaving(true);
    try {
      await api.post("/schemas/import", parsed);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Import failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <Upload size={14} className="text-[#006080]" />
          <h3 className="font-bold text-slate-900 text-sm">Import Schema</h3>
        </div>
        <div className="p-5 space-y-3">
          {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
          <div className="flex items-center gap-2">
            <button onClick={() => fileRef.current?.click()} className="h-8 px-3 text-xs font-medium border border-slate-200 rounded-md hover:bg-slate-50 text-slate-600">
              Choose JSON file
            </button>
            <span className="text-xs text-slate-400">or paste JSON below</span>
            <input ref={fileRef} type="file" accept=".json,application/json" onChange={handleFile} className="hidden" />
          </div>
          <textarea
            value={json}
            onChange={e => setJson(e.target.value)}
            rows={8}
            placeholder='{"name": "My Schema", "fields": [...], ...}'
            className="w-full px-3 py-2 text-xs font-mono border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] resize-none"
          />
        </div>
        <div className="px-5 pb-4 flex gap-2">
          <button onClick={onClose} className="flex-1 h-8 border border-slate-200 text-sm text-slate-600 rounded-md hover:bg-slate-50">Cancel</button>
          <button onClick={handleImport} disabled={saving || !json.trim()} className="flex-1 h-8 bg-[#006080] text-white text-sm font-medium rounded-md disabled:opacity-50">
            {saving ? "Importing…" : "Import Schema"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function SchemaList() {
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteError, setDeleteError] = useState("");
  const [companies, setCompanies] = useState([]);
  const [allocateTarget, setAllocateTarget] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";

  const fetchSchemas = () => {
    setLoading(true);
    const calls = [api.get("/schemas")];
    if (isSuperAdmin) calls.push(api.get("/admin/companies"));
    Promise.all(calls)
      .then(([r, c]) => {
        setSchemas(r.data);
        if (c) setCompanies(c.data.filter(co => co.is_active));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchSchemas(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this schema? This cannot be undone.")) return;
    setDeleteError("");
    try {
      await api.delete(`/schemas/${id}`);
      fetchSchemas();
    } catch (err) {
      setDeleteError(err.response?.data?.detail || "Failed to delete schema.");
      setTimeout(() => setDeleteError(""), 4000);
    }
  };

  const handleToggleStatus = async (schema) => {
    try {
      await api.put(`/schemas/${schema.id}/${schema.status === "approved" ? "draft" : "approve"}`);
      fetchSchemas();
    } catch {}
  };

  const handleDuplicate = async (schema) => {
    try {
      await api.post(`/schemas/${schema.id}/duplicate`);
      fetchSchemas();
    } catch {}
  };

  const handleExport = async (schema) => {
    try {
      const r = await api.get(`/schemas/${schema.id}/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${schema.name.replace(/\s+/g, "_")}.schema.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Document Schemas</h2>
          <p className="text-sm text-slate-500 mt-0.5">Configure extraction templates for different document types.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImport(true)}
            data-testid="import-schema-button"
            className="flex items-center gap-1.5 h-9 px-3 text-sm font-medium border border-slate-200 bg-white text-slate-600 rounded-md hover:bg-slate-50 transition-colors"
          >
            <Upload size={13} /> Import
          </button>
          <Link
            to="/schemas/new"
            data-testid="create-schema-button"
            className="flex items-center gap-2 h-9 px-4 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors"
          >
            <Plus size={14} /> New Schema
          </Link>
        </div>
      </div>

      {deleteError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle size={14} className="flex-shrink-0" /> {deleteError}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" /></div>
      ) : schemas.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-lg">
          <Database size={32} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500 mb-3">No schemas yet</p>
          <Link to="/schemas/new" className="h-9 px-4 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66] transition-colors inline-flex items-center gap-2">
            <Plus size={14} /> Create your first schema
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {schemas.map(schema => {
            const enabledCount = schema.fields?.filter(f => f.enabled !== false).length || 0;
            const disabledCount = (schema.fields?.length || 0) - enabledCount;
            const isGlobal = schema.scope === "global";
                    const scopeLabel = isGlobal ? "Global" : (schema.scope === "company" || !schema.scope) ? "Company" : null;
            return (
              <div key={schema.id} data-testid={`schema-card-${schema.id}`} className="bg-white border border-slate-200 rounded-lg p-5 hover:-translate-y-0.5 hover:shadow-sm transition-all">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-8 h-8 rounded-md flex-shrink-0 flex items-center justify-center ${isGlobal ? "bg-purple-50 text-purple-600" : "bg-[#E5F0F2] text-[#006080]"}`}>
                      {isGlobal ? <Globe size={14} /> : <FileText size={14} />}
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-slate-900 truncate">{schema.name}</h3>
                      <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${STATUS_BADGE[schema.status] || STATUS_BADGE.draft}`}>
                          {schema.status}
                        </span>
                        {isGlobal ? (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600">Global</span>
                        ) : scopeLabel ? (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-400">{scopeLabel}</span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                {schema.description && <p className="text-xs text-slate-500 mb-3 line-clamp-2">{schema.description}</p>}

                <div className="flex items-center gap-2 text-xs mb-4 flex-wrap">
                  <span className="text-slate-400">{schema.document_count || 0} docs</span>
                  {enabledCount > 0 && (
                    <span data-testid={`schema-enabled-count-${schema.id}`} className="px-1.5 py-0.5 rounded-full bg-[#E5F0F2] text-[#006080] font-medium">
                      {enabledCount} active
                    </span>
                  )}
                  {disabledCount > 0 && (
                    <span data-testid={`schema-disabled-count-${schema.id}`} className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-400 font-medium">
                      {disabledCount} off
                    </span>
                  )}
                  {isGlobal && schema.allocated_company_ids?.length > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-500 font-medium">
                      {schema.allocated_company_ids.length} companies
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-1.5 flex-wrap">
                  <Link
                    to={`/schemas/${schema.id}`}
                    data-testid={`edit-schema-${schema.id}`}
                    className="flex items-center gap-1 h-7 px-2 text-xs font-medium border border-slate-200 rounded-md bg-white hover:bg-slate-50 transition-colors"
                  >
                    <Edit size={10} /> Edit
                  </Link>
                  <button
                    onClick={() => handleToggleStatus(schema)}
                    data-testid={`toggle-schema-status-${schema.id}`}
                    className={`flex items-center gap-1 h-7 px-2 text-xs font-medium rounded-md transition-colors ${
                      schema.status === "approved"
                        ? "border border-amber-200 text-amber-700 bg-amber-50"
                        : "border border-[#006080] text-[#006080] bg-white hover:bg-[#E5F0F2]"
                    }`}
                  >
                    <Check size={10} /> {schema.status === "approved" ? "Draft" : "Approve"}
                  </button>
                  <button
                    onClick={() => handleDuplicate(schema)}
                    data-testid={`duplicate-schema-${schema.id}`}
                    title="Duplicate"
                    className="flex items-center gap-1 h-7 px-2 text-xs font-medium border border-slate-200 rounded-md bg-white hover:bg-slate-50 transition-colors text-slate-500"
                  >
                    <Copy size={10} />
                  </button>
                  <button
                    onClick={() => handleExport(schema)}
                    data-testid={`export-schema-${schema.id}`}
                    title="Export JSON"
                    className="flex items-center gap-1 h-7 px-2 text-xs font-medium border border-slate-200 rounded-md bg-white hover:bg-slate-50 transition-colors text-slate-500"
                  >
                    <Download size={10} />
                  </button>
                  {isSuperAdmin && isGlobal && (
                    <button
                      onClick={() => setAllocateTarget(schema)}
                      data-testid={`allocate-schema-${schema.id}`}
                      title="Allocate to Companies"
                      className="flex items-center gap-1 h-7 px-2 text-xs font-medium border border-purple-200 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors text-purple-600"
                    >
                      <Users size={10} /> Allocate
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(schema.id)}
                    data-testid={`delete-schema-${schema.id}`}
                    title="Delete"
                    className="flex items-center h-7 px-2 text-xs font-medium border border-slate-200 rounded-md bg-white hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors text-slate-400"
                  >
                    <Trash2 size={10} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {allocateTarget && (
        <AllocateModal
          schema={allocateTarget}
          companies={companies}
          onClose={() => setAllocateTarget(null)}
          onSave={() => { setAllocateTarget(null); fetchSchemas(); }}
        />
      )}

      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onSave={() => { setShowImport(false); fetchSchemas(); }}
        />
      )}
    </div>
  );
}
